import { useMemo } from 'react';
import { useMenu, MenuType } from './useMenu';
import {
  UilPuzzlePiece,
  Icon
} from '@iconscout/react-unicons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface Route {
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: "font-awesome" | "feather" | "unicons";
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  next?: boolean;
  roles?: string[];
  labelDisabled?: boolean;
}

export interface RouteItems {
  label: string;
  horizontalNavLabel?: string;
  icon: Icon;
  labelDisabled?: boolean;
  pages: Route[];
  megaMenu?: boolean;
  active?: boolean;
  roles?: string[];
}

// Função que converte a hierarquia de menus para o formato de rotas
const buildRoutesFromMenuHierarchy = (menus: MenuType[]): RouteItems[] => {
  // Processar cada menu raiz para gerar uma categoria de menu
  return menus.map(rootMenu => {
    // Verificar se o menu possui submenus
    const hasSubMenus = rootMenu.subMenus && rootMenu.subMenus.length > 0;
    const isLabelMenu = rootMenu.label === true;

    // Construir páginas para este menu
    let menuPages: Route[];

    if (!hasSubMenus) {
      // Menu sem submenus - adicionar como página única
      menuPages = [{
        name: rootMenu.descricao,
        icon: rootMenu.icon ?? getDefaultIcon(),
        iconSet: rootMenu.iconSet,
        active: true,
        flat: true,
        path: rootMenu.routePath || "/",
        pathName: rootMenu.routePathName
      }];
    } else if (isLabelMenu) {
      // Para menus com label=true, tratar submenus diretamente sem aninhar
      menuPages = rootMenu.subMenus.map(submenu => {
        const hasSubSubMenus = submenu.subMenus && submenu.subMenus.length > 0;

        const submenuItem: Route = {
          name: submenu.descricao,
          icon: submenu.icon ?? getDefaultIcon(),
          iconSet: submenu.iconSet,
          active: true,
          flat: !hasSubSubMenus,
          path: !hasSubSubMenus ? submenu.routePath : undefined,
          pathName: !hasSubSubMenus ? submenu.routePathName : undefined
        };

        // Adicionar submenus de terceiro nível, se existirem
        if (hasSubSubMenus) {
          submenuItem.pages = submenu.subMenus.map(subsubmenu => ({
            name: subsubmenu.descricao,
            path: subsubmenu.routePath,
            pathName: subsubmenu.routePathName,
            active: true,
            flat: true
          }));
        }

        return submenuItem;
      });
    } else {
      // Para menus regulares com submenus, criar estrutura expansível
      menuPages = [{
        name: rootMenu.descricao,
        icon: rootMenu.icon ?? getDefaultIcon(),
        iconSet: rootMenu.iconSet,
        active: true,
        flat: false,
        pages: rootMenu.subMenus.map(submenu => {
          const hasSubSubMenus = submenu.subMenus && submenu.subMenus.length > 0;

          // Se não tem submenus, este é o último nível - não incluir ícone
          if (!hasSubSubMenus) {
            return {
              name: submenu.descricao,
              active: true,
              flat: true,
              path: submenu.routePath,
              pathName: submenu.routePathName
            };
          }

          // Se tem submenus, incluir ícone neste nível e construir submenus
          const submenuItem: Route = {
            name: submenu.descricao,
            icon: submenu.icon ?? getDefaultIcon(),
            iconSet: submenu.iconSet,
            active: true,
            flat: false,
            pages: submenu.subMenus.map(subsubmenu => ({
              name: subsubmenu.descricao,
              path: subsubmenu.routePath,
              pathName: subsubmenu.routePathName,
              active: true,
              flat: true
            }))
          };

          return submenuItem;
        })
      }];
    }

    // Criar uma categoria para este menu raiz
    return {
      label: rootMenu.descricao,
      active: true,
      labelDisabled: !rootMenu.label,
      icon: getIconForMenuCategory(rootMenu),
      iconSet: rootMenu.iconSet,
      megaMenu: true,
      pages: menuPages
    };
  });
};

// Função helper para determinar ícone padrão (caso não venha do backend)
const getDefaultIcon = (): string => 'file-text';

// Função para obter o ícone da categoria baseado no menu
const getIconForMenuCategory = (menu: MenuType): Icon => {
  // Implementar lógica para mapear ícones se necessário
  // Por enquanto, usar UilPuzzlePiece como padrão
  return UilPuzzlePiece;
};

// Hook principal que retorna o sitemap dinâmico
export const useDynamicSitemap = () => {
  const { data: menusData, isLoading, error } = useMenu();

  const dynamicRoutes = useMemo(() => {
    if (!menusData?.data || !menusData.isSuccess) {
      return [];
    }

    // Obter as rotas dinâmicas do backend
    const backendRoutes = buildRoutesFromMenuHierarchy(menusData.data);

    return backendRoutes;
  }, [menusData]);

  return {
    routes: dynamicRoutes,
    isLoading,
    error,
    hasData: !!menusData?.data?.length
  };
};
